import { useMemo } from 'react';

interface UseCircularProgressReturn {
  svgProps: {
    width: number;
    height: number;
    viewBox: string;
    style: React.CSSProperties;
  };
  circleProps: {
    cx: number;
    cy: number;
    r: number;
    strokeWidth: number;
    style: React.CSSProperties;
  };
}

/**
 * useCircularProgressAnimation
 *
 * This custom React hook provides all logic and animated state for rendering
 * an infinite animated circular progress spinner using SVG.
 *
 * @param size Number - Diameter of the spinner in pixels.
 * @returns An object with:
 *  - svgProps: props for the SVG element (width, height, viewBox).
 *  - circleProps: props for the <circle> element (refs, geometry, dash styling).
 *  - including CSS variables to be used in SCSS.
 *
 * @example
 * const { svgProps, circleProps } = useCircularProgressAnimation(40);
 * return (
 *   <svg {...svgProps}><circle {...circleProps} /></svg>
 * );
 */
export function useCircularProgressAnimation(
  size: number
): UseCircularProgressReturn {
  const thickness = Math.max(2, Math.min(6, Math.round(size * 0.09))); // closer to MUI
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;

  // MUI ratios for sweep
  const minSweep = 0.0079 * circumference;
  const maxSweep = 0.706 * circumference;
  const midOffset = -0.278 * circumference;
  const maxOffset = -0.984 * circumference;

  // These CSS variables are used in the animation keyframes
  const svgProps = useMemo(
    () => ({
      width: size,
      height: size,
      viewBox: `0 0 ${size} ${size}`,
      style: {
        '--circular-circumference': `${circumference}`,
        '--circular-min-sweep': `${minSweep}`,
        '--circular-max-sweep': `${maxSweep}`,
        '--circular-mid-offset': `${midOffset}`,
        '--circular-max-offset': `${maxOffset}`,
      } as React.CSSProperties,
    }),
    [size, circumference, minSweep, maxSweep, midOffset, maxOffset]
  );

  const circleProps = useMemo(
    () => ({
      cx: size / 2,
      cy: size / 2,
      r: radius,
      strokeWidth: thickness,
      style: {
        fill: 'none',
        stroke: 'currentColor',
        strokeLinecap: 'round',
        // All animation handled by CSS
        animation: 'circular-progress-indeterminate 1.4s ease-in-out infinite',
      } as React.CSSProperties,
    }),
    [size, radius, thickness]
  );

  return { svgProps, circleProps };
}
